/**
 * Feedback API Client
 * 
 * Submits feedback to Cloudflare D1 via /api/feedback endpoint.
 * Automatically collects rich metadata for debugging and analysis.
 * Integrates with LogRocket for session replay correlation.
 * 
 * Architecture:
 * - Frontend: Collects metadata, calls /api/feedback
 * - Cloudflare Function: Validates, stores in D1, notifies Slack
 * - D1: Primary storage (source of truth)
 * - Slack: Secondary notification (best-effort)
 * - LogRocket: Session replay correlation (non-blocking)
 * 
 * @example
 * ```typescript
 * // Submit general feedback
 * await submitFeedback({
 *   username: 'user@example.com',
 *   type: 'general',
 *   message: 'Great app!',
 *   url: window.location.href,
 *   userAgent: navigator.userAgent,
 * });
 * 
 * // Submit bug report with diagnostics
 * await submitFeedback({
 *   username: 'user@example.com',
 *   type: 'bug',
 *   message: 'App crashes on startup',
 *   diagnostics: await collectDiagnosticData(),
 * });
 * ```
 */

import { supabase } from '@/lib/supabase';
import { trackFeedbackSubmission } from '@/lib/logrocket';
import type {
  FeedbackRequest,
  FeedbackSuccessResponse,
  FeedbackApiRawResponse,
  FeedbackType,
  BrowserInfo,
} from '@/types/api';

/**
 * Get current route from window location
 */
function getCurrentRoute(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.pathname + window.location.search;
}

/**
 * Detect device type based on screen width
 */
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' | null {
  if (typeof window === 'undefined') return null;
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Get screen size
 */
function getScreenSize(): string | null {
  if (typeof window === 'undefined') return null;
  return `${window.innerWidth}x${window.innerHeight}`;
}

/**
 * Get app version from environment
 */
function getAppVersion(): string {
  return (import.meta.env.VITE_APP_VERSION as string) || (import.meta.env.PACKAGE_VERSION as string) || 'unknown';
}

/**
 * Validate session ID format and entropy
 */
export function isValidSessionId(sessionId: string | null | undefined): boolean {
  if (!sessionId || typeof sessionId !== 'string') return false;
  
  const pattern = /^session_\d+_[a-z0-9]+$/;
  if (!pattern.test(sessionId)) {
    console.warn('[Feedback] Invalid session ID format:', sessionId);
    return false;
  }
  
  const parts = sessionId.split('_');
  if (parts.length !== 3 || parts[2].length < 9) {
    console.warn('[Feedback] Insufficient session ID entropy');
    return false;
  }
  
  return true;
}

/**
 * Generate or retrieve session ID
 */
export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  
  let sessionId: string | null = null;
  
  try {
    sessionId = sessionStorage.getItem('feedback_session_id');
  } catch (error: any) {
    console.warn('[Feedback] sessionStorage not available:', error.message);
  }
  
  if (sessionId && isValidSessionId(sessionId)) {
    return sessionId;
  }
  
  let randomPart: string;
  
  if (typeof crypto !== 'undefined') {
    if (crypto.randomUUID) {
      randomPart = crypto.randomUUID().replace(/-/g, '');
    } else if (crypto.getRandomValues) {
      const array = new Uint32Array(5);
      crypto.getRandomValues(array);
      randomPart = Array.from(array, dec => 
        dec.toString(36).padStart(7, '0')
      ).join('').substring(0, 32);
    } else {
      randomPart = Array.from({ length: 4 }, () => 
        Math.random().toString(36).substring(2, 10)
      ).join('').substring(0, 32);
    }
  } else {
    randomPart = Array.from({ length: 4 }, () => 
      Math.random().toString(36).substring(2, 10)
    ).join('').substring(0, 32);
  }
  
  sessionId = `session_${Date.now()}_${randomPart}`;
  
  if (!isValidSessionId(sessionId)) {
    console.error('[Feedback] Failed to generate valid session ID');
    return null;
  }
  
  try {
    sessionStorage.setItem('feedback_session_id', sessionId);
  } catch (error: any) {
    console.warn('[Feedback] Cannot persist session ID:', error.message);
  }
  
  return sessionId;
}

/**
 * Get detailed browser information
 */
function getBrowserInfo(): BrowserInfo | null {
  if (typeof window === 'undefined' || !navigator) return null;
  
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: [...navigator.languages],
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
  };
}

/**
 * Get current authenticated user from Supabase
 */
async function getCurrentUser() {
  if (!supabase) return null;
  
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.warn('Failed to get current user:', error);
      return null;
    }
    return user;
  } catch (error) {
    console.warn('Error fetching current user:', error);
    return null;
  }
}

/**
 * Normalize feedback type for backward compatibility
 */
function normalizeFeedbackType(type: string): FeedbackType {
  if (type === 'feedback') return 'general';
  const allowedTypes: FeedbackType[] = ['bug', 'suggestion', 'general'];
  return (allowedTypes as string[]).includes(type) ? (type as FeedbackType) : 'general';
}

/**
 * Validate and normalize email address
 */
function normalizeEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  
  const placeholders = ['anonymous', 'none', 'n/a', 'na', 'system-generated'];
  const normalized = email.toLowerCase().trim();
  
  if (placeholders.includes(normalized)) return null;
  if (!normalized.includes('@')) return null;
  
  return email.trim();
}

/**
 * Submit feedback to Cloudflare D1 via /api/feedback
 * 
 * This function:
 * 1. Collects rich metadata (route, device, version, session, etc.)
 * 2. Gets current authenticated user from Supabase (if logged in)
 * 3. Sends to /api/feedback which stores in D1 and notifies Slack
 * 4. Tracks submission in LogRocket for session replay correlation (non-blocking)
 * 
 * @param data - Feedback data including username, type, message, and optional diagnostics
 * @param data.username - User's name or email identifier
 * @param data.type - Feedback type: 'bug', 'suggestion', or 'general'
 * @param data.message - The feedback message content
 * @param data.email - Optional user email (auto-filled if authenticated)
 * @param data.url - Current page URL (auto-collected)
 * @param data.userAgent - Browser user agent (auto-collected)
 * @param data.diagnostics - Optional diagnostic information for bug reports
 * @returns Promise resolving to submission result with feedback ID and delivery method
 * 
 * @example
 * ```typescript
 * // Simple feedback
 * const result = await submitFeedback({
 *   username: 'user@example.com',
 *   type: 'general',
 *   message: 'Love the new features!',
 * });
 * console.log(`Feedback submitted with ID: ${result.feedbackId}`);
 * 
 * // Bug report with diagnostics
 * const result = await submitFeedback({
 *   username: 'user@example.com',
 *   type: 'bug',
 *   message: 'App crashes on startup',
 *   diagnostics: await collectDiagnosticData(),
 * });
 * ```
 * 
 * @throws Error if API submission fails and local storage fallback is unavailable
 */
export async function submitFeedback(data: FeedbackRequest): Promise<FeedbackSuccessResponse> {
  const currentUser = await getCurrentUser();

  const payload = {
    user: data.username || currentUser?.email || 'Anonymous',
    email: normalizeEmail(data.email || currentUser?.email),
    userId: currentUser?.id || null,
    type: normalizeFeedbackType(data.type),
    text: data.message,
    url: data.url,
    route: getCurrentRoute(),
    deviceType: getDeviceType(),
    screenSize: getScreenSize(),
    appVersion: getAppVersion(),
    sessionId: getSessionId(),
    userAgent: data.userAgent || navigator?.userAgent || null,
    browserInfo: getBrowserInfo(),
    diagnostics: data.diagnostics || null,
  };

  try {
    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json() as FeedbackApiRawResponse;

    if (!response.ok) {
      throw new Error(result.error || 'Failed to submit feedback');
    }

    const feedbackId = result.feedbackId != null ? String(result.feedbackId) : '';
    if (!feedbackId) {
      throw new Error('Invalid response: feedback submission returned without an ID');
    }

    console.log('✓ Feedback submitted successfully:', feedbackId);
    
    try {
      trackFeedbackSubmission({
        feedbackId,
        type: payload.type,
        route: payload.route,
        deviceType: payload.deviceType,
        appVersion: payload.appVersion,
        sessionId: payload.sessionId,
        diagnostics: payload.diagnostics,
      });
    } catch (error) {
      console.error('✗ LogRocket: Failed to track feedback submission:', error);
    }

    return {
      success: true,
      feedbackId,
      deliveredVia: result.deliveredVia ?? { api: true },
      message: result.message,
    };
  } catch (error) {
    console.error('✗ Failed to submit feedback:', error);
    throw error;
  }
}
